import { Anchor, Button, Checkbox, Text } from '@mantine/core';
import { IconBuilding } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PhysicalContactForm from 'src/Components/Forms/Contact/PhysicalContactForm/PhysicalContactForm.component';
import { ContactCategory } from 'src/Constants/ContactCategories.constants';
import {
  CreatePhysicalContactFormValues,
  InterlocutorFormValues,
  useCreatePhysicalContactForm,
} from 'src/Hooks/Forms/useCreatePhysicalContactForm';
import useSiretConflictHandler from 'src/Hooks/Modals/useSiretConflictHandler';
import useUpdateContact from 'src/Hooks/Mutations/useUpdateContact';
import useFetchContact from 'src/Hooks/Queries/useFetchContact';
import {
  CompanyContactRole,
  ContactKind,
  CreateContactDtoTitleEnum,
  UpsertInterlocutorDtoTitleEnum,
} from 'src/Services/API/generated';
import { trackContactConvertedToCompany } from 'src/Services/Mixpanel/events';
import { canConvertToCompany } from 'src/Utils/contactKindPreset';

import { Props } from './ContactEditionFormWrapper.types';

const CONVERTED_INTERLOCUTOR_KEY = 'converted-person';

// "Fessart - " -> "Fessart": families typed the company in the last name, with trailing dashes.
const cleanCompanyName = (lastName?: string) => (lastName ?? '').replace(/[\s\-–—]+$/, '').trim();

const ContactEditionFormWrapper = ({ onCreate, onCancel, contactId }: Props) => {
  const { data: contact } = useFetchContact(contactId);
  const { form } = useCreatePhysicalContactForm();
  const handleSiretConflict = useSiretConflictHandler();
  const [isConverting, setIsConverting] = useState(false);
  const { mutate, isLoading } = useUpdateContact({
    onError: error => {
      handleSiretConflict(error);
    },
    onSuccess: () => {
      if (isConverting) {
        trackContactConvertedToCompany({
          kept_main_interlocutor: form.values.interlocutors.some(
            interlocutor => interlocutor.key === CONVERTED_INTERLOCUTOR_KEY
          ),
        });
      }
      onCreate();
    },
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (contact) {
      const messagingChannels = {
        SMS: false,
        EMAIL: false,
        SIGNAL: false,
        WHATSAPP: false,
        MESSENGER: false,
      };

      const paymentMethods: Partial<
        Record<'iban' | 'lydia' | 'paypal' | 'wero', { identifier: string; isFavorite: boolean }>
      > = {};

      contact?.paymentMethods.forEach(method => {
        paymentMethods[method.type.toLowerCase() as keyof typeof paymentMethods] = {
          identifier: method.identifier,
          isFavorite: method.isFavorite,
        };
      });

      contact.messagingChannels.forEach(
        channel =>
          (messagingChannels[
            channel.type as keyof CreatePhysicalContactFormValues['messagingChannels']
          ] = true)
      );

      form.setValues({
        kind: contact.kind,
        title: contact.title as CreateContactDtoTitleEnum,
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        companyName: contact.companyName ?? '',
        siret: contact.siret ?? '',
        siretFound: !!contact.siret,
        websiteUrl: contact.websiteUrl ?? '',
        customerReference: contact.customerReference ?? '',
        usualPaymentMode: contact.usualPaymentMode,
        notes: contact.notes ?? '',
        interlocutors: contact.interlocutors.map(interlocutor => ({
          key: interlocutor.id,
          id: interlocutor.id,
          title: interlocutor.title,
          firstName: interlocutor.firstName ?? '',
          lastName: interlocutor.lastName,
          email: interlocutor.email ?? '',
          phoneNumber: interlocutor.phoneNumber ?? '',
          companyRole: interlocutor.companyRole,
          companyRoleLabel: interlocutor.companyRoleLabel ?? '',
        })),
        email: contact.email,
        phoneNumber: contact.phoneNumber,
        isEmergencyContact: contact.isEmergencyContact,
        messengerValue:
          contact.messagingChannels.find(channel => channel.type === 'MESSENGER')?.identifier ??
          undefined,
        placeId: contact.address?.googlePlaceId ?? undefined,
        familyRelationType: contact.familyRelationType,
        imageUrl: contact.imageUrl,
        imagePath: contact.imagePath,
        category: contact.category,
        subcategory: contact.subcategory,
        messagingChannels: messagingChannels,
        paymentMethods: {
          iban: {
            identifier: paymentMethods.iban?.identifier ?? '',
            isFavorite: paymentMethods.iban?.isFavorite ?? false,
          },
          wero: {
            identifier: paymentMethods.wero?.identifier ?? '',
            isFavorite: paymentMethods.wero?.isFavorite ?? false,
          },
          lydia: {
            identifier: paymentMethods.lydia?.identifier ?? '',
            isFavorite: paymentMethods.lydia?.isFavorite ?? false,
          },
          paypal: {
            identifier: paymentMethods.paypal?.identifier ?? '',
            isFavorite: paymentMethods.paypal?.isFavorite ?? false,
          },
        },
      });
    }
  }, [contact]);

  // The person who was typed in the card ("Olivia Thevignot" in the first name) becomes the
  // main contact of the company. The card keeps its id, so linked expenses follow.
  const convertedInterlocutor = (): InterlocutorFormValues => ({
    key: CONVERTED_INTERLOCUTOR_KEY,
    title: contact?.title as UpsertInterlocutorDtoTitleEnum | undefined,
    firstName: '',
    lastName: (contact?.firstName ?? '').trim(),
    email: contact?.email ?? '',
    phoneNumber: contact?.phoneNumber ?? '',
    companyRole: CompanyContactRole.Main,
    companyRoleLabel: '',
  });

  const startConversion = () => {
    form.setFieldValue('kind', ContactKind.Company);
    form.setFieldValue('companyName', cleanCompanyName(contact?.lastName));
    form.setFieldValue('siret', '');
    form.setFieldValue('siretFound', false);
    form.setFieldValue(
      'interlocutors',
      contact?.firstName?.trim() ? [convertedInterlocutor()] : []
    );
    setIsConverting(true);
  };

  const cancelConversion = () => {
    form.setFieldValue('kind', ContactKind.Person);
    form.setFieldValue('companyName', '');
    form.setFieldValue('interlocutors', []);
    setIsConverting(false);
  };

  const keepsPerson = form.values.interlocutors.some(
    interlocutor => interlocutor.key === CONVERTED_INTERLOCUTOR_KEY
  );

  const toggleKeepPerson = (keep: boolean) => {
    const others = form.values.interlocutors.filter(
      interlocutor => interlocutor.key !== CONVERTED_INTERLOCUTOR_KEY
    );
    if (keep) {
      // Back as main contact: the others keep their own role.
      form.setFieldValue('interlocutors', [
        convertedInterlocutor(),
        ...others.map(other =>
          other.companyRole === CompanyContactRole.Main
            ? { ...other, companyRole: CompanyContactRole.Other, companyRoleLabel: '' }
            : other
        ),
      ]);
    } else {
      form.setFieldValue(
        'interlocutors',
        others.map((other, index) =>
          index === 0 ? { ...other, companyRole: CompanyContactRole.Main } : other
        )
      );
    }
  };

  const personName = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');

  const conversionHeader = isConverting ? (
    <div>
      {contact?.firstName?.trim() && (
        <Checkbox
          checked={keepsPerson}
          onChange={event => toggleKeepPerson(event.currentTarget.checked)}
          label={t('contacts.company.keepAsMain', 'Garder {{name}} comme contact principal', {
            name: contact.firstName.trim(),
          })}
        />
      )}
      <Text size="sm" c="dimmed" mt={8}>
        {t(
          'contacts.company.conversionInfo',
          'La fiche « {{name}} » devient une entreprise. Ses dépenses et liens restent rattachés.',
          { name: personName }
        )}{' '}
        <Anchor component="button" type="button" size="sm" onClick={cancelConversion}>
          {t('contacts.company.cancelConversion', 'Annuler la conversion')}
        </Anchor>
      </Text>
    </div>
  ) : undefined;

  const convertButton =
    contact?.kind === ContactKind.Person &&
    canConvertToCompany(form.values.category as ContactCategory | undefined) ? (
      <Button
        variant="outline"
        leftSection={<IconBuilding size={18} />}
        onClick={startConversion}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('contacts.company.convert', "C'est une entreprise")}
      </Button>
    ) : undefined;

  return (
    <PhysicalContactForm
      onCancel={onCancel}
      form={form}
      onSubmit={form.onSubmit(values => {
        mutate({ contactId, updateData: values });
      })}
      submitButtonLabel={t('editContactForm.editContact', 'Modifier')}
      initialAddressText={contact?.address?.formattedAddress}
      loading={isLoading}
      companyHeader={conversionHeader}
      autoFocusCompanyName={isConverting}
      afterIdentity={convertButton}
    />
  );
};

export default ContactEditionFormWrapper;
